import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  Signal,
} from '@angular/core';
import { Guid, Nillable, valueOrDefault } from '@web/utils';
import { Button } from 'primeng/button';
import {
  FormControlComponent,
  NoDataPipe,
  PrimengButtonDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { map, Observable, of } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Select, SelectFilterEvent } from 'primeng/select';
import { WarehouseReleaseProductFormGroup } from '../../models/warehouse-release-form-data';
import {
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from 'primeng/accordion';
import { warehouseProductSelectItemsWithCurrentSelection } from '../../utils/warehouse-product-select-items-with-current-selection';
import { WarehouseProductSelectItemsProviderService } from '../../providers/warehouse-product-select-items-provider.service';
import { warehouseProductCurrentSelectItem } from '../../utils/warehouse-product-current-select-item';
import { WarehouseProductDataSourceService } from '../../services/warehouse-product-data-source.service';

@Component({
  selector: 'app-warehouse-release-form-product-panel',
  templateUrl: './warehouse-release-form-product-panel.component.html',
  styleUrls: ['./warehouse-release-form-product-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionContent,
    AccordionHeader,
    AccordionPanel,
    AsyncPipe,
    Button,
    FormControlComponent,
    InputText,
    NoDataPipe,
    PrimengButtonDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
  ],
})
export class WarehouseReleaseFormProductPanelComponent implements OnInit {
  /* prettier-ignore */
  private readonly productDataSource = inject(WarehouseProductDataSourceService);
  /* prettier-ignore */
  private readonly productSelectItemsProvider = inject(WarehouseProductSelectItemsProviderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /* prettier-ignore */
  public readonly productFormGroup = input.required<WarehouseReleaseProductFormGroup>();
  public readonly index = input.required<number>();
  public readonly total = input.required<number>();
  public readonly removeClickEvent = output<number>();

  protected readonly panelCount = this.panelCountSignal();
  protected readonly shouldRemove = this.shouldRemoveSignal();
  protected readonly valueOrDefault = valueOrDefault;

  protected currentName$!: Observable<Nillable<string>>;
  protected productSelectItems$: Observable<SelectItem<Guid>[]> = of([]);

  public constructor() {
    effect(() => this.configureProductSelectItemsSource());
  }

  public ngOnInit(): void {
    this.configureCurrentNameSource();
  }

  public handleProductRemoveClick(): void {
    this.removeClickEvent.emit(this.index());
  }

  public handleSelectFilterChange(event: SelectFilterEvent): void {
    this.productSelectItems$ = this.productSelectItems(
      event.filter.toLowerCase(),
    );

    this.changeDetectorRef.detectChanges();
  }

  private configureProductSelectItemsSource(): void {
    this.productSelectItems$ = this.productSelectItems();
    this.changeDetectorRef.detectChanges();
  }

  private currentSelectItem(): Observable<Nillable<SelectItem<Guid>>> {
    const group = this.productFormGroup();

    return warehouseProductCurrentSelectItem({
      control: group.controls.id,
      dataSource: this.productDataSource,
    });
  }

  private productSelectItems(
    query: Nillable<string> = null,
  ): Observable<SelectItem<Guid>[]> {
    const group = this.productFormGroup();

    return warehouseProductSelectItemsWithCurrentSelection({
      control: group.controls.id,
      dataSource: this.productDataSource,
      selectItemsProvider: this.productSelectItemsProvider,
      query,
    });
  }

  private panelCountSignal(): Signal<string> {
    return computed(() => `#${this.index() + 1}`);
  }

  private shouldRemoveSignal(): Signal<boolean> {
    return computed(() => this.total() !== 1);
  }

  private configureCurrentNameSource(): void {
    this.currentName$ = this.currentSelectItem().pipe(
      map((selectItems) => selectItems?.label),
    );
  }
}
